import { CustomerIndustry, CustomerType } from '../enums/customer.enums';
import { Account, AdPlatform, AdPlatformType } from './common.models';
import { UserLanguage, UserRole } from './user.models';

export enum SubscriptionPriceType {
  FixedFee = 'FIXED-FEE',
  AdSpend = 'AD-SPEND',
  AttributedSales = 'ATTRIBUTED-SALES',
  EbayAttributedSales = 'EBAY',
}

export enum SubscriptionPriceDropdownType {
  FixedFee = SubscriptionPriceType.FixedFee,
  AdSpend = SubscriptionPriceType.AdSpend,
  Amazon = 'AMAZON',
  Ebay = 'EBAY',
}

export enum SubscriptionStatus {
  Scheduled = 'SCHEDULED',
  Active = 'ACTIVE',
  Ending = 'ENDING',
  Terminated = 'TERMINATED',
}

export enum PricingModel {
  FixedFee = 'FIXED-FEE',
  VolumeFlatFeeOrPercentage = 'VOLUME-FLAT-FEE-OR-PERCENTAGE',
  FixedFeePlusVolumePercentages = 'FIXED-FEE-PLUS-VOLUME-PERCENTAGES',
  Other = 'OTHER',
}

export enum BillingVolumeType {
  AttributedSales = SubscriptionPriceType.AttributedSales,
  AdSpend = SubscriptionPriceType.AdSpend,
}

export enum AdspertPlanType {
  Lite = 'LITE',
  Standard = 'STANDARD',

  Essential = 'ESSENTIAL',
  Scale = 'SCALE',
  Pro = 'PRO',
  Managed = 'MANAGED',
}

export enum CustomerSegment {
  Enterprise = 'ENTERPRISE',
  Essential = 'ESSENTIAL',
  Pro = 'PRO',
  Scale = 'SCALE',
  Standard = 'STANDARD',
}

export enum SubscriptionInvoicePeriod {
  Monthly = 1,
  Yearly = 12,
}

export type InvoiceLanguage = 'DE' | 'EN';

export type CurrencyPair = `${Currency}-${Currency}`;

export type CurrencyRates = Record<CurrencyPair, number>;

export type InvoiceCurrency = 'EUR' | 'USD' | 'GBP';

export type CurrencyDeprecated = 'HRK';

export const BaseCurrencies = [
  'EUR',
  'USD',
  'JPY',
  'GBP',
  'AUD',
  'CAD',
  'CHF',
  'HKD',
  'SEK',
  'DKK',
  'INR',
  'PLN',
  'MXN',
  'TRY',
  'SGD',
  'BRL',
  'AED',
  'EGP',
] as const;

export type Currency = (typeof BaseCurrencies)[number];

export type ApiCurrency = Currency;

export const AccountGroupAllowedRoles: UserRole[] = ['CUSTOMER-ADMIN', 'USER'];

export interface SubscriptionPrice {
  id: string;
  name: string;
  currency: Currency;
  type: SubscriptionPriceType;
  platforms: AdPlatformType[] | null;
}

export interface PlanConstraints {
  minEndDate: Date;
  currencies: InvoiceCurrency[];
  types: AdspertPlanType[];
}

export interface PlanPriceAddOn {
  featureName: CustomerFeature;
  fixedFee: number;
}

export interface PlanPriceDetails {
  model: PricingModel;
  fixedFee?: number;
  volumeFlatFeeOrPercentage: PlanPriceFlatFee[];
  volumePercentage: PlanPriceVolumePercentage[];
  addOns: PlanPriceAddOn[];
}

export interface PlanPriceVolumePercentage {
  platforms: AdPlatformType[];
  volumeType: BillingVolumeType;
  tiers: PlanPriceVolumePercentageTier[];
}

export interface PlanPriceVolumePercentageTier {
  threshold: number;
  percentage: number;
}

export interface PlanPriceFlatFee {
  platforms: AdPlatformType[] | null;
  volumeType: BillingVolumeType;
  threshold: number;
  flatFee: number;
  percentage: number;
}

export interface CustomerFeatureStatus {
  name: CustomerFeature;
  available: boolean;
  inUse: boolean;
}

export interface CustomerSubscription {
  type?: AdspertPlanType;
  startDate: Date | string;
  endDate: Date | string | null;
  currency?: InvoiceCurrency;
  nextInvoiceDate?: Date | null;
  nextInvoiceAmount?: number;
  prices?: SubscriptionPrice[];
  priceIds?: string[];
  preview?: boolean;
  status?: SubscriptionStatus;
  billingVolume?: BillingVolume[];
  pricingDetails?: PlanPriceDetails;
  legacy?: boolean;
  paymentDueDays?: number;
  renewalMonthsNext?: number;
  renewalMonthsCurrent?: number;
  renewalDate?: Date;
  renewalDiscounts?: {
    months: number;
    percentOff: number;
  }[];
  features?: CustomerFeatureStatus[];
  // for post requests
  renewalMonths?: SubscriptionInvoicePeriod;

  // non api
  unavailable?: boolean;
}

export interface BillingVolume {
  type: BillingVolumeType;
  platforms: AdPlatformType[];
  amountCurrentMonth: number;
  amountLastMonth: number;
}

export interface CustomerSubscriptionConstraints {
  prices: SubscriptionPrice[];
  currencies: InvoiceCurrency[];
  minStartDate: Date | null;
  minEndDate: Date;
}

export interface CustomerAccountTrial {
  // searchable
  adspertAccountId: number;
  // searchable
  accountId: number;
  // searchable
  accountName: number;
  platform: AdPlatform;
  endDate: Date;
}

export interface AccountTrialConstraints {
  minEndDate: Date;
  validAccounts: Account[];
}
export enum CustomerFeature {
  TriggerNotifications = 'trigger-notifications',
  HidePromotionsPage = 'hide-promotions-page',
  SecondGoalForPG = 'performance-groups-second-goal',
  CriterionAdditionAdvanced = 'criterion-addition-advanced',
  MerchantSpring = 'merchant-spring',
  ProfitOptimization = 'profit-optimization',
  SearchOptimization = 'search-optimization',
  MinMaxBid = 'min-max-bid',
  MinBid = 'min-bid',
}
export interface CustomerUser {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  lang: UserLanguage;
  role: UserRole;
  notifications: boolean;
  features?: CustomerFeature[];
}

export interface Customer {
  customerId: number;
  type: CustomerType;
  mainCustomerId?: number;
  mainCustomerName?: string;
  billingSubsidiariesCount: number;
  agencyClientsCount: number;
  agencyAdminsCount: number;
  name: string;
  email?: string;
  address1?: string;
  address2?: string;
  zip: string;
  city: string;
  countryCode: string;
  addressComplete?: boolean;
  invoiceLanguage: InvoiceLanguage;
  // Currency for self-service checkouts. sys-admin only
  checkoutCurrency?: InvoiceCurrency;
  subscriptionCurrency?: InvoiceCurrency;
  metricCurrency?: Currency;
  orderNumber: string;
  vatId: string;
  vatIdValid: boolean | null;
  segment?: CustomerSegment;

  subscriptionStatus?: SubscriptionStatus;
  subscriptionStart?: Date;
  subscriptionEnd?: Date;
  planType?: AdspertPlanType;
  trial?: boolean;
  trialStart?: Date;
  trialEnd?: Date;
  canceledDue?: Date;
  canceled?: boolean;
  allowStartOptimization?: boolean;
  mccAutoConnect?: boolean;
  accountManagerId?: number;
  accountManagerFirstName?: string;
  accountManagerLastName?: string;
  archived?: boolean | null;
  createdAt?: Date;
  stripeCustomerId?: string;
  salesforceId?: string;
  vip?: boolean | null;
  access?: UserRole;
  notifications?: boolean;
  features?: CustomerFeature[];
  usersCount?: number;
  accountsCount?: number;
  unreadNotificationsCount?: number;
  isDemo?: boolean;
  industry?: CustomerIndustry | string;
  phone?: string;
  monthlySalesPotential?: number;
}

export interface CustomerListFilters {
  accessGrantedTo?: number;
}

export enum CancellationStatus {
  None,
  User,
  Managed,
  Restart = 4,
  Start = 8,
  Undo = 16,
}
